import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RecentTonesTimeline = () => {
  const [selectedTone, setSelectedTone] = useState(null);

  const recentTones = [
    {
      id: 1,
      date: new Date(Date.now() - 86400000), // Yesterday
      tone: 'G Major',
      aspect: 'Sun ♂ Jupiter',
      intensity: 85,
      duration: '4:32',
      thumbnail: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=100&h=100&fit=crop&crop=center',
      description: 'Harmonious expansion energy'
    },
    {
      id: 2,
      date: new Date(Date.now() - 172800000), // 2 days ago
      tone: 'D Minor',
      aspect: 'Moon ♂ Saturn',
      intensity: 62,
      duration: '3:45',
      thumbnail: 'https://images.pexels.com/photos/1629236/pexels-photo-1629236.jpeg?w=100&h=100&fit=crop&crop=center',
      description: 'Reflective introspection'
    },
    {
      id: 3,
      date: new Date(Date.now() - 259200000), // 3 days ago
      tone: 'F♯ Major',
      aspect: 'Venus ♂ Mars',
      intensity: 91,
      duration: '5:18',
      thumbnail: 'https://images.pixabay.com/photo/2016/06/02/02/33/triangles-1430105_1280.jpg?w=100&h=100&fit=crop&crop=center',
      description: 'Passionate creative flow'
    },
    {
      id: 4,
      date: new Date(Date.now() - 345600000), // 4 days ago
      tone: 'B♭ Major',
      aspect: 'Mercury ♂ Uranus',
      intensity: 73,
      duration: '2:56',
      thumbnail: 'https://images.unsplash.com/photo-1518837695005-2083093ee35b?w=100&h=100&fit=crop&crop=center',
      description: 'Innovative communication'
    },
    {
      id: 5,
      date: new Date(Date.now() - 432000000), // 5 days ago
      tone: 'E Minor',
      aspect: 'Sun ♂ Neptune',
      intensity: 56,
      duration: '6:12',
      thumbnail: 'https://images.pexels.com/photos/1323550/pexels-photo-1323550.jpeg?w=100&h=100&fit=crop&crop=center',
      description: 'Mystical dreamy atmosphere'
    }
  ];

  const handleTonePlay = (tone) => {
    setSelectedTone(tone);
    console.log('Playing tone:', tone.tone);
  };

  const formatDate = (date) => {
    const today = new Date();
    const diffTime = Math.abs(today - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return 'Yesterday';
    if (diffDays <= 7) return `${diffDays} days ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="glass bg-surface/50 backdrop-blur-cosmic rounded-2xl p-6 shadow-elevation-3">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-heading font-semibold text-text-primary">
            Recent Tones
          </h2>
          <p className="text-sm text-text-secondary mt-1">
            Your astrological musical journey
          </p>
        </div>
        <Button
          variant="ghost"
          iconName="History"
          onClick={() => console.log('View full history')}
        >
          View All
        </Button>
      </div>

      {/* Timeline Container */}
      <div className="relative">
        {/* Timeline Line */}
        <div className="absolute left-6 top-0 bottom-0 w-px bg-gradient-to-b from-primary via-secondary to-accent opacity-30"></div>

        {/* Timeline Items */}
        <div className="space-y-6">
          {recentTones.map((tone, index) => (
            <div key={tone.id} className="relative flex items-start space-x-4">
              {/* Timeline Dot */}
              <div className={`relative z-10 w-3 h-3 rounded-full border-2 border-background ${
                index === 0 ? 'bg-primary animate-gentle-pulse' : 'bg-surface'
              }`}></div>

              {/* Tone Card */}
              <div className={`flex-1 glass bg-surface/30 backdrop-blur-sm rounded-xl p-4 transition-cosmic hover:bg-surface/50 cursor-pointer ${
                selectedTone?.id === tone.id ? 'ring-2 ring-primary' : ''
              }`} onClick={() => handleTonePlay(tone)}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    {/* Thumbnail */}
                    <div className="w-12 h-12 rounded-lg overflow-hidden bg-surface">
                      <img
                        src={tone.thumbnail}
                        alt={`${tone.tone} visualization`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = '/assets/images/no_image.png';
                        }}
                      />
                    </div>

                    {/* Tone Info */}
                    <div>
                      <div className="flex items-center space-x-2">
                        <h3 className="text-sm font-heading font-medium text-text-primary">
                          {tone.tone}
                        </h3>
                        <span className="text-xs font-mono text-accent">
                          {tone.aspect}
                        </span>
                      </div>
                      <p className="text-xs text-text-secondary mt-1">
                        {tone.description}
                      </p>
                      <div className="flex items-center space-x-3 mt-2">
                        <span className="text-xs text-text-secondary font-mono">
                          {formatDate(tone.date)}
                        </span>
                        <span className="text-xs text-text-secondary">
                          {tone.duration}
                        </span>
                        <div className="flex items-center space-x-1">
                          <Icon name="Zap" size={12} color="#F59E0B" />
                          <span className="text-xs text-accent">{tone.intensity}%</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Play Button */}
                  <Button
                    variant="ghost"
                    iconName={selectedTone?.id === tone.id ? "Pause" : "Play"}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTonePlay(tone);
                    }}
                    className="w-8 h-8 p-0"
                  />
                </div>

                {/* Intensity Bar */}
                <div className="mt-3">
                  <div className="w-full h-1 bg-surface rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-primary to-accent transition-all duration-500"
                      style={{ width: `${tone.intensity}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mt-6 pt-4 border-t border-border/50">
        <div className="grid grid-cols-2 gap-3">
          <Button
            variant="outline"
            iconName="Calendar"
            onClick={() => console.log('Select date range')}
            fullWidth
          >
            Custom Range
          </Button>
          <Button
            variant="outline"
            iconName="Download"
            onClick={() => console.log('Export timeline')}
            fullWidth
          >
            Export
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RecentTonesTimeline;